/**
 * The pouch in 3D with a label printed on it, hovering a little above its own shadow. It bobs and
 * sways on a slow loop and leans a few degrees toward a mouse pointer. With reduced motion it is
 * drawn once, still. The landing hero, the comparison pages and the empty upload step all show it.
 *
 * The pouch keeps its own proportions whatever the slot's shape. The slot only decides how much
 * room is around it, so a narrow column gets a smaller pouch rather than a thinner one.
 *
 * The scene loads after the first paint and fades in. Until then the slot holds its final size, so
 * nothing shifts. A browser without hardware WebGL gets the same label drawn flat.
 */
import * as stylex from "@stylexjs/stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { examplePouch } from "./example.ts";
import type { LabelEdition } from "./render/pouch.ts";
import { Tones } from "./render/Tones.tsx";

/** Where the landing hero puts the pouch beside its headline. */
const TWO_UP = "@media (min-width: 720px)";
const styles = {
  figure: {
    kogj98: "x1ghz6dp",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1173whf",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  caption: {
    kGuDYH: "xl4dl4e",
    kMwMTN: "x1jty25b",
    k9WMMc: "x2b8uid",
    $$css: true
  },
  slot: {
    kVAEAm: "x1n2onr6",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  hero: {
    ks0D6T: "x11gisft",
    kFhvOy: "x7v7x1q xibkab0",
    kOBAk4: "x1fg1c7i x1b1h59g",
    $$css: true
  },
  column: {
    kOBAk4: "x1uhxotw",
    $$css: true
  },
  fill: {
    kVAEAm: "x10l6tqk",
    kpwlN0: "x10a8y8t",
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    $$css: true
  },
  flat: {
    kVIFPx: "x19kjcj4",
    $$css: true
  },
  flatHidden: {
    k1xSpc: "x1s85apg",
    $$css: true
  },
  canvas: {
    kSiTet: "xg01cxk",
    k1ekBW: "x19991ni",
    kIyJzY: "x937rjr",
    $$css: true
  },
  canvasShown: {
    kSiTet: "x1hc1fzr",
    $$css: true
  }
};

/** The share of the slot's width the pouch takes in each placement. */
const FILL = {
  hero: 0.72,
  column: 0.82
} as const;
type Mode = "pending" | "live" | "flat";

/* Started when the page's code loads rather than after the first render, so the 3D is fetched
   while React is still building the page. */
const SCENE = import("./render/pouchShot.ts").catch(() => null);
export function PouchShot({
  placement,
  edition = examplePouch,
  label = "An example Lattice pouch made for Jordan S., printed with their formula and a physician's signature.",
  testId = "pouch",
  caption
}: {
  /** `hero` for the landing hero. `column` for a sidebar or any narrow slot. */
  readonly placement: keyof typeof FILL;
  readonly edition?: LabelEdition;
  /** What the picture shows, for a screen reader. */
  readonly label?: string;
  readonly testId?: string;
  /** A line under the pouch, such as one saying it is an example. */
  readonly caption?: ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const flat = useRef<HTMLCanvasElement>(null);
  const [mode, setMode] = useState<Mode>("pending");
  useEffect(() => {
    const slot = host.current;
    const surface = canvas.current;
    if (!slot || !surface) return;
    let unmount: (() => void) | null = null;
    let gone = false;
    void SCENE.then(scene => {
      if (!scene) return null;
      const live = scene.mountPouchShot(slot, surface, edition, FILL[placement]);
      if (!live && flat.current) scene.paintPouchLabel(slot, flat.current, edition);
      return live;
    }).catch(() => null).then(mounted => {
      if (gone) {
        mounted?.();
        return;
      }
      unmount = mounted;
      setMode(mounted ? "live" : "flat");
    });
    return () => {
      gone = true;
      unmount?.();
    };
  }, [edition, placement]);
  const shot = <div ref={host} {...stylex.props(styles.slot, styles[placement])} role="img" aria-label={label} data-testid={testId} data-mode={mode}>
      {/* Only without 3D, and the pouch's own label rather than a photograph, which showed a
          different pouch. */}
      <canvas ref={flat} data-testid="pouch-flat-label" aria-hidden="true" {...{
      0: {
        className: "x10l6tqk x10a8y8t xh8yej3 x5yr21d x19kjcj4"
      },
      1: {
        className: "x10l6tqk x10a8y8t xh8yej3 x5yr21d x19kjcj4 x1s85apg"
      }
    }[!!(mode !== "flat") << 0]} />
      <canvas ref={canvas} aria-hidden="true" {...{
      0: {
        className: "x10l6tqk x10a8y8t xh8yej3 x5yr21d xg01cxk x19991ni x937rjr"
      },
      1: {
        className: "x10l6tqk x10a8y8t xh8yej3 x5yr21d x19991ni x937rjr x1hc1fzr"
      }
    }[!!(mode === "live") << 0]} />
      <Tones />
    </div>;
  if (caption === undefined) return shot;
  return <figure className="x1ghz6dp x78zum5 xdt5ytf x1173whf xh8yej3">
      {shot}
      <figcaption className="xl4dl4e x1jty25b x2b8uid">{caption}</figcaption>
    </figure>;
}